import React, { useState, useEffect } from 'react';
import { Table, Form, Button, Modal, Card } from "react-bootstrap";
import { Link, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faEye, faPen, faPlus, faTrash, faFilter, faSync 
} from '@fortawesome/free-solid-svg-icons';
import useFetch from "../../../hooks/useFetch";
import RequireRoles from '../../../components/RequireRoles';
import PaginatedItems from '../../../components/administracao/Pagination';

export default function GerenciarTermos() {
    // https://create-react-app.dev/docs/adding-custom-environment-variables/
    const { params } = useParams(); // pega o que vem depois da barra na url
    const { get, del } = useFetch();

    const defaultState = {
        termos: [],
        count: 0,
        page: 1,
        filtroAtivo: false,
        filtros: {
            palavra: ""
        },
        mostrarModal: false,
        registroAExcluir: {
            id_palavra1: '',
            id_palavra2: ''
        },
        loading: true
    }

    // Roda quando a página é carregada https://reactjs.org/docs/hooks-reference.html#lazy-initial-state
    const [state, setState] = useState(() => {
        return defaultState;
    });

    useEffect(() => {
        async function initialize() {
            const [termos, count] = await buscarTermos({});
            setState({ ...state, termos: termos, count: count, loading: false });
        }
        initialize();
    }, [params]);

    // função de paginação
    async function handlePageChange(pageNumber) {
        window.scrollTo(0, 0);
        let filtroPaginacao = { page: pageNumber };
        if (state.filtroAtivo) {
            filtroPaginacao = { ...filtroPaginacao, page: pageNumber };
        }
        let [termos, count] = await buscarTermos(filtroPaginacao);
        setState({ ...state, page: pageNumber, termos: termos, count: count, loading: false });
    }

    // Busca os dados do banco
    async function buscarTermos(filtros) {
        if (!state.loading) setState({ ...state, loading: true });
        // filtros = { nome: "nome_cat", descricao: "descricao" }
        let query = "?";
        if (filtros && Object.keys(filtros).length > 0) {
            for (const key in filtros) {
                if (filtros[key] !== "") {
                    query = query.concat(key, "=", filtros[key], "&");
                }
            }
        }

        // no fim fica: nome_cat=nome_cat&descricao=descricao&
        try {
            let response = await get('/termo' + query);
            if (response.ok) {
                let dados = response.result;
                return [dados.data, dados.count];
            } else {
                console.error('Erro no buscarTermos()');
                return [];
            }
        } catch (error) {
            console.error('Erro no buscarTermos():', error);
        }
    }

    // Função acionada quando o usuário clica em buscar
    async function filtrarTermos() {
        // adiciona os dados no objeto filtros se eles não forem vazios
        // Busca os dados do banco com os filtros
        let [termos, count] = await buscarTermos(state.filtros);
        // Atualiza o estado com os dados buscados
        setState({ ...state, termos: termos, count: count, filtroAtivo: true, loading: false });
    }

    async function limparFiltros() {
        // Busca os dados do banco sem filtros
        let [termos, count] = await buscarTermos({});
        // Atualiza o estado com os dados buscados e reseta os valores dos campos de filtro
        setState({ ...state, filtros: defaultState.filtros, termos: termos, count: count, filtroAtivo: false, loading: false });
    }

    function handleChangeFiltros(event) {
        let target = event.target;
        let value = target.value;
        let name = target.name;

        // se for dropdown, pega o valor selecionado
        if (target.type === 'select-one') {
            value = target.options[target.selectedIndex].value;
        }

        setState({
            ...state,
            filtros: {
                ...state.filtros,
                [name]: value
            }
        });
    }

    async function excluirTermo() {
        let termo = state.registroAExcluir;
        try {
            let response = await del('/termo/' + termo.id_palavra1 + '/' + termo.id_palavra2);
            if (response.ok) {
                let [termos, count] = await buscarTermos({});
                setState({ ...state, termos: termos, count: count, mostrarModal: false, registroAExcluir: {}, loading: false });
            }
            else {
                console.error('Erro no excluirTermo()');
            }
        } catch (error) {
            console.error('Erro no excluirTermo():', error);
        }
    }

    return (
        <div className="admin-page-container">
            <div className="admin-header-container">
                <div className="title-group">
                    <h2 className="admin-title">Gerenciamento de Termos</h2>
                    <p className="admin-subtitle">Gerencie as relações de sinônimos e termos técnicos</p>
                </div>
                <Link to="/admin/termos/criar/criar" className="btn-add-new">
                    <FontAwesomeIcon icon={faPlus} className="me-2" /> Novo Termo
                </Link>
            </div>

            <Card className="filter-card-admin mb-4">
                <Card.Body className="p-4">
                    <Form onSubmit={(e) => { e.preventDefault(); filtrarTermos(); }}>
                        <div className="row align-items-end">
                            <Form.Group className="col-md-8">
                                <Form.Label className="label-admin">Termo</Form.Label>
                                <Form.Control
                                    className="input-moderno"
                                    placeholder="Buscar por palavra..."
                                    name="palavra"
                                    value={state.filtros.palavra}
                                    onChange={handleChangeFiltros}
                                />
                            </Form.Group>
                            <div className="col-md-4 d-flex gap-2">
                                <Button type="button" className="btn-apply-filters w-100" onClick={filtrarTermos}>
                                    <FontAwesomeIcon icon={faFilter} className="me-2" /> Filtrar
                                </Button>
                                <Button type="button" className="btn-clear-filters" onClick={limparFiltros} title="Limpar">
                                    <FontAwesomeIcon icon={faSync} />
                                </Button>
                            </div>
                        </div>
                    </Form>
                </Card.Body>
            </Card>

            <Card className="table-card-admin">
                <div className="table-responsive">
                    <Table className="modern-table mb-0">
                        <thead>
                            <tr>
                                <th>Termo Principal</th>
                                <th>Termo Relacionado</th>
                                <th className="text-center">Opções</th>
                            </tr>
                        </thead>
                        <tbody>
                            {state.loading ? (
                                <tr><td colSpan="3" className="text-center py-5"><div className="spinner-border text-teal"></div></td></tr>
                            ) : state.termos.length > 0 ? (
                                state.termos.map((item, index) => (
                                    <tr key={index}>
                                        <td data-label="Termo Principal" className="fw-bold">{item.Termo1.palavra}</td>
                                        <td data-label="Termo Relacionado">{item.Termo2.palavra}</td>
                                        <td data-label="Opções">
                                            <div className="d-flex justify-content-center gap-2">
                                                <Link className="action-icon view" to={`/admin/termos/${item.id_palavra1}/${item.id_palavra2}`} state={{ acao: "" }}><FontAwesomeIcon icon={faEye} /></Link>
                                                <Link className="action-icon edit" to={`/admin/termos/${item.id_palavra1}/${item.id_palavra2}`} state={{ acao: "Editar" }}><FontAwesomeIcon icon={faPen} /></Link>
                                                <RequireRoles allowedRoles={['Desenvolvedor']}>
                                                    <button className="action-icon delete" onClick={() => setState({ ...state, mostrarModal: true, registroAExcluir: item })}><FontAwesomeIcon icon={faTrash} /></button>
                                                </RequireRoles>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr><td colSpan="3" className="text-center py-4">Nenhum termo encontrado.</td></tr>
                            )}
                        </tbody>
                    </Table>
                </div>
                {state.termos.length > 0 && (
                    <div className="card-footer bg-white border-0 py-3 d-flex justify-content-center">
                        <PaginatedItems itemCount={state.count} itemsPerPage={20} handlePageChange={handlePageChange} />
                    </div>
                )}
            </Card>

            <Modal show={state.mostrarModal} onHide={() => setState({ ...state, mostrarModal: false })} centered className="modern-modal">
                <Modal.Header closeButton className="modal-header-teal">
                    <Modal.Title className="text-white fs-5">Excluir Termo</Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-4">
                    <p className="mb-0">Deseja excluir a relação entre <strong>{state.registroAExcluir.Termo1?.palavra}</strong> e <strong>{state.registroAExcluir.Termo2?.palavra}</strong>?</p>
                </Modal.Body>
                <Modal.Footer className="border-0 p-4 pt-0">
                    <Button variant="light" className="btn-modal-cancel" onClick={() => setState({ ...state, mostrarModal: false })}>Cancelar</Button>
                    <Button variant="danger" className="px-4" onClick={excluirTermo}>Confirmar Exclusão</Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}